import React, { useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import Avatar from '@/components/misc/Avatar';
import { MentionUser } from '@/types/notification';
import { cn } from '@/lib/utils';
import { useTranslations } from 'next-intl';

interface MentionAutocompleteProps {
  users: MentionUser[];
  isVisible: boolean;
  position: { bottom: number; left: number };
  onUserSelect: (user: MentionUser) => void;
  onClose: () => void;
  searchQuery: string;
  selectedIndex: number;
  onIndexChange: (index: number) => void;
  textareaRef?: React.RefObject<HTMLTextAreaElement>;
}

export function MentionAutocomplete({
  users,
  isVisible,
  position,
  onUserSelect,
  onClose,
  searchQuery,
  selectedIndex,
  onIndexChange,
  textareaRef,
}: MentionAutocompleteProps) {
  const dropdownRef = useRef<HTMLDivElement>(null);
  const t = useTranslations('mentions');

  const filteredUsers = users
    .filter(
      (user) =>
        user.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        user.email.toLowerCase().includes(searchQuery.toLowerCase()),
    )
    .slice(0, 5);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!isVisible || filteredUsers.length === 0) return;

      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          onIndexChange(selectedIndex < filteredUsers.length - 1 ? selectedIndex + 1 : 0);
          break;
        case 'ArrowUp':
          e.preventDefault();
          onIndexChange(selectedIndex > 0 ? selectedIndex - 1 : filteredUsers.length - 1);
          break;
        case 'Enter':
          e.preventDefault();
          if (selectedIndex >= 0 && filteredUsers[selectedIndex]) {
            onUserSelect(filteredUsers[selectedIndex]);
          }
          break;
        case 'Escape':
          e.preventDefault();
          onClose();
          break;
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isVisible, filteredUsers, selectedIndex, onIndexChange, onUserSelect, onClose]);
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        onClose();
      }
    };

    if (isVisible) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isVisible, onClose]);
  useEffect(() => {
    if (dropdownRef.current && selectedIndex >= 0) {
      const selectedElement = dropdownRef.current.children[selectedIndex] as HTMLElement;
      if (selectedElement) {
        selectedElement.scrollIntoView({
          behavior: 'smooth',
          block: 'nearest',
        });
      }
    }
  }, [selectedIndex]);

  if (!isVisible || filteredUsers.length === 0) {
    return null;
  }

  const getDropdownWidth = () => {
    if (textareaRef?.current) {
      const textareaWidth = textareaRef.current.offsetWidth;
      return Math.max(200, Math.min(textareaWidth, 400));
    }
    return 300;
  };

  const dropdown = (
    <div
      ref={dropdownRef}
      className="fixed z-[9999] overflow-hidden rounded-md border border-gray-200 bg-white shadow-lg"
      style={{
        bottom: position.bottom,
        left: position.left,
        width: `${getDropdownWidth()}px`,
        pointerEvents: 'auto',
      }}
    >
      <div className="py-1">
        {filteredUsers.map((user, index) => (
          <button
            key={user.id}
            type="button"
            className={cn(
              'flex w-full items-center gap-3 px-3 py-2 text-left transition-colors hover:bg-gray-50 focus:bg-gray-50 focus:outline-none',
              selectedIndex === index && 'bg-blue-50',
            )}
            onClick={() => onUserSelect(user)}
            onMouseEnter={() => onIndexChange(index)}
            aria-label={t('autocomplete.selectUser', { name: user.name })}
          >
            <Avatar name={user.name} />
            <div className="min-w-0 flex-1">
              <div className="truncate text-sm font-medium text-gray-900">{user.name}</div>
              <div className="truncate text-xs text-gray-500">{user.email}</div>
            </div>
          </button>
        ))}
      </div>
      <div className="border-t border-gray-100 bg-gray-50 px-3 py-2">
        <div className="text-xs text-gray-500">{t('autocomplete.navigationHint')}</div>
      </div>
    </div>
  );

  return typeof window !== 'undefined' ? createPortal(dropdown, document.body) : null;
}
